// First-connect display-name modal — Phase 7 Block B.
//
// Plain HTML overlay (same idiom as hud-hints.ts). Shown when the
// initial snapshot reports an empty display_name; the player must
// pick a name before any other UI is interactive. Submit calls
// setDisplayName via the network; the displayNameSet durable
// envelope is what drives the modal's close.
//
// Validation is performed live as the user types (via shared/
// display-name.ts) so the submit button can stay disabled for invalid
// input. The server is the authoritative gate; this is purely UX.

import { buildSetDisplayNameMessage, type ClientMessage, type ServerMessage } from "./types.ts";
import { validateDisplayName, DISPLAY_NAME_MAX_LEN } from "../../shared/display-name.ts";

export type DisplayNameModalDeps = {
  send: (msg: ClientMessage) => void;
  // Called when the modal opens / closes; main.ts uses this to suspend
  // movement input via LocalController.setModal.
  setModalSuppressed: (suppressed: boolean) => void;
};

export class DisplayNameModal {
  private root: HTMLDivElement | null = null;
  private input: HTMLInputElement | null = null;
  private submit: HTMLButtonElement | null = null;
  private validation: HTMLDivElement | null = null;
  private serverError: HTMLDivElement | null = null;
  private _isOpen = false;

  constructor(private readonly deps: DisplayNameModalDeps) {}

  get isOpen(): boolean {
    return this._isOpen;
  }

  open(initialValue: string = ""): void {
    if (this._isOpen) return;
    this._isOpen = true;
    this.deps.setModalSuppressed(true);

    const root = document.createElement("div");
    root.id = "display-name-modal";
    root.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,0.75);" +
      "display:flex;align-items:center;justify-content:center;" +
      "z-index:100000;font-family:monospace;color:#eef";

    const card = document.createElement("div");
    card.style.cssText =
      "background:#12122a;border:2px solid #4455cc;border-radius:6px;" +
      "padding:24px 28px;min-width:320px;max-width:420px;" +
      "box-shadow:0 8px 32px rgba(0,0,0,0.6)";
    root.appendChild(card);

    const title = document.createElement("h2");
    title.textContent = "Welcome — what should we call you?";
    title.style.cssText = "margin:0 0 8px 0;font-size:16px;color:#eef";
    card.appendChild(title);

    const help = document.createElement("div");
    help.textContent = `3-${DISPLAY_NAME_MAX_LEN} characters: letters, numbers, underscore, hyphen.`;
    help.style.cssText = "font-size:11px;color:#88a;margin-bottom:14px";
    card.appendChild(help);

    const input = document.createElement("input");
    input.type = "text";
    input.id = "display-name-input";
    input.value = initialValue;
    input.maxLength = DISPLAY_NAME_MAX_LEN;
    input.autocomplete = "off";
    input.spellcheck = false;
    input.style.cssText =
      "width:100%;padding:8px 10px;font-family:monospace;font-size:14px;" +
      "background:#0a0a1c;border:1px solid #4455cc;border-radius:4px;" +
      "color:#eef;outline:none;box-sizing:border-box";
    card.appendChild(input);

    const validation = document.createElement("div");
    validation.id = "display-name-validation";
    validation.style.cssText = "font-size:11px;color:#f88;min-height:14px;margin-top:6px";
    card.appendChild(validation);

    const serverError = document.createElement("div");
    serverError.id = "display-name-server-error";
    serverError.style.cssText = "font-size:11px;color:#f88;min-height:14px;margin-top:2px";
    card.appendChild(serverError);

    const submit = document.createElement("button");
    submit.id = "display-name-submit";
    submit.type = "button";
    submit.textContent = "Continue";
    submit.style.cssText =
      "margin-top:14px;width:100%;padding:10px 12px;font-family:monospace;font-size:13px;" +
      "background:#2a2a55;border:1px solid #4455cc;border-radius:4px;" +
      "color:#eef;cursor:pointer";
    card.appendChild(submit);

    document.body.appendChild(root);

    this.root = root;
    this.input = input;
    this.submit = submit;
    this.validation = validation;
    this.serverError = serverError;

    // Stop key events from bubbling to the window-level input router so
    // typing K / G / B / etc. doesn't open other panels under the modal.
    input.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.code === "Enter") {
        e.preventDefault();
        this.trySubmit();
      }
    });
    input.addEventListener("keyup", (e) => e.stopPropagation());
    input.addEventListener("input", () => this.refreshValidation());

    submit.addEventListener("click", () => this.trySubmit());

    // Don't yank focus on every render — refreshValidation runs on every
    // keystroke. Initial focus only.
    queueMicrotask(() => input.focus());
    this.refreshValidation();
  }

  // Drive open + close from server envelopes. snapshot with empty
  // displayName triggers the first-connect open; displayNameSet for the
  // local player closes; errors render inline so the player can retry.
  applyServerMessage(msg: ServerMessage, selfId: string): void {
    if (!this._isOpen && msg.type === "snapshot" && msg.payload.playerId === selfId) {
      const self = msg.payload.players.find((p) => p.playerId === selfId);
      if (self !== undefined && self.displayName === "") this.open();
      return;
    }
    if (!this._isOpen) return;
    if (msg.type === "displayNameSet" && msg.payload.playerId === selfId) {
      this.close();
      return;
    }
    if (msg.type === "error" && this.serverError) {
      this.serverError.textContent = msg.payload.message;
      this.setSubmitting(false);
    }
  }

  private close(): void {
    this._isOpen = false;
    if (this.root && this.root.parentElement) {
      this.root.parentElement.removeChild(this.root);
    }
    this.root = null;
    this.input = null;
    this.submit = null;
    this.validation = null;
    this.serverError = null;
    this.deps.setModalSuppressed(false);
  }

  private currentValue(): string {
    return this.input?.value.trim() ?? "";
  }

  private refreshValidation(): void {
    const value = this.currentValue();
    const result = validateDisplayName(value);
    if (this.validation) {
      // Empty input shows nothing (don't yell at the player who hasn't
      // typed anything yet); other failures show the reason.
      this.validation.textContent = value.length === 0 || result.ok ? "" : result.reason;
    }
    if (this.serverError) this.serverError.textContent = "";
    if (this.submit) {
      this.submit.disabled = !result.ok;
      this.submit.style.opacity = result.ok ? "1" : "0.5";
      this.submit.style.cursor = result.ok ? "pointer" : "not-allowed";
    }
  }

  private trySubmit(): void {
    const value = this.currentValue();
    const result = validateDisplayName(value);
    if (!result.ok) {
      this.refreshValidation();
      return;
    }
    this.setSubmitting(true);
    this.deps.send(buildSetDisplayNameMessage(value));
  }

  private setSubmitting(submitting: boolean): void {
    if (this.submit) {
      this.submit.disabled = submitting;
      this.submit.textContent = submitting ? "..." : "Continue";
    }
    if (this.input) this.input.disabled = submitting;
  }
}
